/**
 * Renders a Markdown article body (`posts.body_md_en` / `body_md_es`).
 *
 * The parsed document from `~/lib/markdown` is mapped onto React elements one
 * node at a time — no HTML string is ever produced, so no `dangerouslySetInnerHTML`
 * anywhere in the article pipeline. Typography is per block rather than a uniform
 * container gap, so headings get real breathing room above them and related copy
 * stays tight.
 *
 * Used by the public article page and by the admin editor's live preview, which
 * is why it takes the "References" label as a prop instead of reaching for i18n:
 * the console is English-only, the site is bilingual.
 */

import { cn } from '@/lib/utils';
import type { MdBlock, MdInline, MdList, PostDocument } from '~/lib/markdown';

function InlineNodes({ nodes }: { nodes: MdInline[] }) {
  return (
    <>
      {nodes.map((node, i) => {
        switch (node.kind) {
          case 'strong':
            return (
              <strong key={i} className="font-semibold text-foreground">
                <InlineNodes nodes={node.children} />
              </strong>
            );
          case 'em':
            return (
              <em key={i} className="italic">
                <InlineNodes nodes={node.children} />
              </em>
            );
          case 'del':
            return (
              <del key={i} className="line-through">
                <InlineNodes nodes={node.children} />
              </del>
            );
          case 'code':
            return (
              <code key={i} className="rounded bg-surface-pale px-1 py-0.5 font-mono text-[0.9em] text-foreground">
                {node.value}
              </code>
            );
          case 'link':
            // An unsafe or malformed target keeps its text but loses the link.
            return node.href ? (
              <a
                key={i}
                href={node.href}
                className="rounded-sm text-primary underline underline-offset-2 transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <InlineNodes nodes={node.children} />
              </a>
            ) : (
              <InlineNodes key={i} nodes={node.children} />
            );
          case 'break':
            return <br key={i} />;
          default:
            // A bare string, not a wrapper element: plain prose must render as
            // exactly the same DOM it did before Markdown authoring existed.
            return node.value;
        }
      })}
    </>
  );
}

function ListNode({ list, nested = false }: { list: MdList; nested?: boolean }) {
  const Tag = list.ordered ? 'ol' : 'ul';
  return (
    <Tag
      className={cn(
        'flex flex-col gap-1.5 pl-5 text-justify text-[15px] leading-[1.6] text-foreground-soft marker:text-primary',
        list.ordered ? 'list-decimal' : 'list-disc',
        nested ? 'mt-1.5' : 'my-3'
      )}
    >
      {list.items.map((item, i) => (
        <li key={i}>
          <InlineNodes nodes={item.children} />
          {item.lists.map((sublist, j) => (
            <ListNode key={j} list={sublist} nested />
          ))}
        </li>
      ))}
    </Tag>
  );
}

function BlockNode({ block, isLead, referencesLabel }: { block: MdBlock; isLead: boolean; referencesLabel: string }) {
  switch (block.kind) {
    case 'h2':
      return (
        <h2 className="mt-8 mb-3 font-display text-xl md:text-[25px] leading-[1.3] font-bold text-foreground">
          <InlineNodes nodes={block.children} />
        </h2>
      );
    case 'h3':
      return (
        <h3 className="mt-6 mb-2 font-display text-base md:text-[17px] leading-6 font-bold text-foreground">
          <InlineNodes nodes={block.children} />
        </h3>
      );
    case 'quote':
      return (
        <blockquote className="my-5 rounded-2xl border border-border bg-surface-pale px-6 py-5">
          {block.paragraphs.map((paragraph, i) => (
            <p
              key={i}
              className={cn(
                'font-display text-lg md:text-xl italic leading-[1.45] text-foreground text-balance',
                i > 0 && 'mt-3'
              )}
            >
              <InlineNodes nodes={paragraph} />
            </p>
          ))}
        </blockquote>
      );
    case 'list':
      return <ListNode list={block} />;
    case 'code':
      return (
        <pre className="my-3 overflow-x-auto rounded-2xl border border-border bg-surface-pale px-5 py-4 font-mono text-[13px] leading-[1.6] text-foreground-soft">
          <code>{block.value}</code>
        </pre>
      );
    case 'hr':
      return <hr className="my-8 h-px w-full border-0 bg-border" />;
    case 'refs':
      return (
        <div className="mt-12 flex flex-col gap-3 rounded-2xl border border-border bg-card px-6 py-6 shadow-card-soft">
          <p className="font-mono text-[11px] tracking-[1.1px] text-primary uppercase">{referencesLabel}</p>
          <div className="h-px w-full bg-border" />
          <ol className="flex list-decimal flex-col gap-2.5 pl-4 marker:font-mono marker:text-[11px] marker:text-muted-foreground">
            {block.items.map((item, i) => (
              <li key={i} className="pl-1 text-[13px] leading-[1.55] text-muted-foreground">
                <InlineNodes nodes={item} />
              </li>
            ))}
          </ol>
        </div>
      );
    default:
      return (
        <p
          className={
            isLead
              ? 'mb-3.5 text-justify text-[16px] leading-[1.55] font-medium text-foreground md:text-[17px]'
              : 'mb-3.5 text-justify text-[15px] leading-[1.6] text-foreground-soft'
          }
        >
          <InlineNodes nodes={block.children} />
        </p>
      );
  }
}

/** Takes the parsed document rather than the Markdown string: the article page
    also needs it for the reading time, and parsing twice per render is waste. */
export function PostBody({
  doc,
  referencesLabel,
  className,
}: {
  doc: PostDocument;
  referencesLabel: string;
  className?: string;
}) {
  // An opening paragraph reads as a standfirst; one buried mid-article is just
  // body copy. `findIndex` returning -1 falls through to "no lead" on its own.
  const firstParagraph = doc.blocks.findIndex((block) => block.kind === 'p');
  const leadIndex = firstParagraph <= 1 ? firstParagraph : -1;

  return (
    // first:*:mt-0 — the opening block sits flush under the cover instead of stacking margins
    <div className={cn('flex flex-col first:*:mt-0', className)}>
      {doc.blocks.map((block, i) => (
        <BlockNode key={i} block={block} isLead={i === leadIndex} referencesLabel={referencesLabel} />
      ))}
    </div>
  );
}
